/**
 * Kloutforce mirror of apps/katalog-app/src/lib/signupIntentFinalize.ts.
 *
 * Kept byte-compatible with the Katalog client so the same backend
 * contract (POST /api/account-signup/finalize) governs both apps.
 * See the Katalog file for the full doctrine — no tokens, hashes,
 * or magic links are ever logged; permanent failures are not retried;
 * transient failures use bounded exponential backoff.
 */

export type FinalizeOutcomeKind =
  | 'verified'
  | 'already_verified'
  | 'attached'
  | 'already_attached'
  | 'waiting_for_paid_order'
  | 'no_intent'
  | 'unauthorized'
  | 'conflict'
  | 'transient_exhausted'
  | 'http_error'
  | 'no_token';

export interface FinalizeOutcome {
  kind: FinalizeOutcomeKind;
  status?: number;
  attempts?: number;
  intentId?: string | null;
  attachedOrderId?: string | null;
}

const MAX_ATTEMPTS = 3;
const BASE_BACKOFF_MS = 250;

function isTransientStatus(status: number): boolean {
  return status === 408 || status === 425 || status === 429 || (status >= 500 && status < 600);
}

function classifyResponseBody(
  body: { ok?: boolean; intent_id?: string | null; invite_outcome?: string | null; attach_outcome?: string | null; attached_order_id?: string | null } | null,
): FinalizeOutcomeKind {
  if (!body) return 'verified';
  const attach = (body.attach_outcome ?? '').toString();
  if (attach === 'attached') return 'attached';
  if (attach === 'already_attached') return 'already_attached';
  if (attach === 'waiting_for_paid_order') return 'waiting_for_paid_order';
  if (body.intent_id == null) return 'no_intent';
  return 'verified';
}

export interface FinalizeArgs {
  backendUrl: string;
  accessToken: string;
  authUserId: string;
  fetchImpl?: typeof fetch;
  sleepImpl?: (ms: number) => Promise<void>;
}

const defaultSleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

export async function finalizeSignupIntentWithRetry(
  args: FinalizeArgs,
): Promise<FinalizeOutcome> {
  const fetchImpl = args.fetchImpl ?? fetch;
  const sleepImpl = args.sleepImpl ?? defaultSleep;
  const url = `${args.backendUrl}/api/account-signup/finalize`;

  let lastTransient: FinalizeOutcome = { kind: 'transient_exhausted', attempts: 0 };

  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    try {
      const res = await fetchImpl(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${args.accessToken}`,
        },
        body: JSON.stringify({}),
      });
      if (res.ok) {
        let body: any = null;
        try { body = await res.json(); } catch { /* tolerate empty body */ }
        return {
          kind: classifyResponseBody(body),
          status: res.status,
          attempts: attempt,
          intentId: body?.intent_id ?? null,
          attachedOrderId: body?.attached_order_id ?? null,
        };
      }
      if (res.status === 401 || res.status === 403) {
        return { kind: 'unauthorized', status: res.status, attempts: attempt };
      }
      if (res.status === 409) {
        return { kind: 'conflict', status: res.status, attempts: attempt };
      }
      if (isTransientStatus(res.status)) {
        lastTransient = {
          kind: 'transient_exhausted',
          status: res.status,
          attempts: attempt,
        };
        if (attempt < MAX_ATTEMPTS) {
          await sleepImpl(BASE_BACKOFF_MS * Math.pow(2, attempt - 1));
          continue;
        }
        return lastTransient;
      }
      return { kind: 'http_error', status: res.status, attempts: attempt };
    } catch {
      lastTransient = { kind: 'transient_exhausted', attempts: attempt };
      if (attempt < MAX_ATTEMPTS) {
        await sleepImpl(BASE_BACKOFF_MS * Math.pow(2, attempt - 1));
        continue;
      }
      return lastTransient;
    }
  }
  return lastTransient;
}